import React, { useEffect, useState, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const CATEGORIES = ['ALL', 'RECEPTION', 'MAINTENANCE', 'AMENITY', 'OTHER'];
const STATUS_OPTIONS = ['', 'OPEN', 'IN_PROGRESS', 'DONE', 'CANCELLED'];
const STATUS_LABELS = { '': 'All', OPEN: 'Open', IN_PROGRESS: 'In Progress', DONE: 'Done', CANCELLED: 'Cancelled' };

const SERVICE_TYPES = {
  RECEPTION: ['TAXI_REQUEST', 'WAKE_UP_CALL', 'LUGGAGE_ASSISTANCE', 'LATE_CHECKOUT', 'ROOM_CHANGE', 'EXTRA_KEY'],
  MAINTENANCE: ['AC_NOT_WORKING', 'TV_ISSUE', 'PLUMBING_ISSUE', 'LIGHT_ISSUE', 'WIFI_ISSUE', 'SAFE_ISSUE'],
  AMENITY: ['SPA_BOOKING', 'GYM_ACCESS', 'POOL_TOWELS', 'LAUNDRY', 'NEWSPAPER', 'FLOWERS'],
  OTHER: ['GENERAL_INQUIRY', 'COMPLAINT', 'SPECIAL_REQUEST']
};

const HK_TYPES = ['EXTRA_TOWELS', 'WATER_BOTTLES', 'ROOM_CLEANING', 'EXTRA_BLANKET', 'EXTRA_PILLOWS', 'IRON_BOX', 'HAIR_DRYER', 'TOILETRIES', 'DO_NOT_DISTURB', 'EXTRA_HANGERS', 'BABY_COT', 'ROOM_SPRAY'];

const statusColor = (s) => {
  if (s === 'OPEN') return '#92400e';
  if (s === 'IN_PROGRESS') return '#1d4ed8';
  if (s === 'DONE') return '#166534';
  return 'var(--theme-text-secondary)';
};

const CreatedByBadge = ({ type, name, timestamp }) => {
  const isGuest = type === 'GUEST';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <span style={{ fontSize: '0.72rem', padding: '1px 6px', borderRadius: 4, fontWeight: 600, display: 'inline-block', width: 'fit-content',
        background: isGuest ? '#dbeafe' : '#fef3c7', color: isGuest ? '#1e40af' : '#92400e' }}>
        {isGuest ? 'Guest' : 'Staff'}
      </span>
      <span style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>{name || '-'}</span>
      {timestamp && <span style={{ fontSize: '0.68rem', color: 'var(--theme-text-muted)' }}>
        {new Date(timestamp).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, day: '2-digit', month: 'short' })}
      </span>}
    </div>
  );
};

function PmsGuestRequests({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [hkRequests, setHkRequests] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('OPEN');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // New Request form state
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState('SERVICE'); // SERVICE or HK
  const [occupiedRooms, setOccupiedRooms] = useState([]);
  const [formData, setFormData] = useState({ room_id: '', request_category: 'RECEPTION', request_type: '', quantity: 1, priority: 'NORMAL', remarks: '' });
  const [formLoading, setFormLoading] = useState(false);

  // Module 3 — optional billing on a Service Request.
  const [guestServices, setGuestServices] = useState([]);
  const [billable, setBillable] = useState(false);
  const [billingServiceId, setBillingServiceId] = useState('');
  const [fulfilmentMode, setFulfilmentMode] = useState('CHARGE_TO_ROOM');
  const [paymentMode, setPaymentMode] = useState('CASH');
  const [billingAmount, setBillingAmount] = useState(0);
  const [billingBusyId, setBillingBusyId] = useState(null); // request_id currently posting, for the retry button

  const headers = useCallback((withJson = false) => ({
    Authorization: `Bearer ${token}`,
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) { setHkRequests([]); setServiceRequests([]); return; }
    setLoading(true);
    setError('');
    try {
      const statusQs = statusFilter ? `status=${encodeURIComponent(statusFilter)}` : '';
      const categoryQs = categoryFilter && categoryFilter !== 'ALL' ? `&category=${encodeURIComponent(categoryFilter)}` : '';
      const [hkRes, svcRes] = await Promise.all([
        fetch(`/api/v1/pms/housekeeping/requests?${statusQs}`, { headers: headers() }),
        fetch(`/api/v1/pms/housekeeping/service-requests?${statusQs}${categoryQs}`, { headers: headers() })
      ]);
      const hkData = await hkRes.json();
      const svcData = await svcRes.json();
      if (!hkRes.ok) throw new Error(hkData?.error || 'Failed to load HK requests');
      if (!svcRes.ok) throw new Error(svcData?.error || 'Failed to load service requests');
      setHkRequests(Array.isArray(hkData) ? hkData : []);
      setServiceRequests(Array.isArray(svcData) ? svcData : []);
    } catch (err) {
      setError(err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, statusFilter, categoryFilter, headers]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const loadOccupiedRooms = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch('/api/v1/pms/guest-requests/occupied-rooms', { headers: headers() });
      const data = await res.json();
      if (res.ok) setOccupiedRooms(Array.isArray(data) ? data : []);
    } catch (_) {}
  }, [selectedProperty, headers]);

  const loadGuestServices = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch('/api/v1/pms/guest-service-master', { headers: headers() });
      const data = await res.json();
      if (res.ok) setGuestServices(Array.isArray(data) ? data : []);
    } catch (_) {}
  }, [selectedProperty, headers]);

  const openForm = (type) => {
    setFormType(type);
    setFormData({ room_id: '', request_category: 'RECEPTION', request_type: '', quantity: 1, priority: 'NORMAL', remarks: '' });
    setBillable(false);
    setBillingServiceId('');
    setFulfilmentMode('CHARGE_TO_ROOM');
    setPaymentMode('CASH');
    setBillingAmount(0);
    setShowForm(true);
    loadOccupiedRooms();
    if (type === 'SERVICE') loadGuestServices();
  };

  const onBillingServiceChange = (id) => {
    setBillingServiceId(id);
    const svc = guestServices.find(s => String(s.id) === String(id));
    if (svc) setBillingAmount(Number(svc.rate_amount || 0) * (Number(formData.quantity) || 1));
  };

  // Posts the actual charge (and, for PAY_NOW, the payment) to the folio —
  // reuses the existing generic postings endpoint exactly as the folio screen
  // already does (PmsCommandCenter.js resolves stay -> folio the same way).
  // Returns true if fully posted.
  const postBillingForRequest = async (requestRow, { stayId, revenueCode, postingType, sourceModule, amount, mode, payMode, skipDebit = false }) => {
    const folioRes = await fetch(`/api/v1/pms/stays/${stayId}/folio`, { headers: headers() });
    const folioData = await folioRes.json();
    if (!folioRes.ok || !folioData?.folio?.folio_id) throw new Error(folioData?.error || 'Could not resolve an open folio for this stay');
    const folioId = folioData.folio.folio_id;
    const refBody = { reference_type: 'GUEST_SERVICE_REQUEST', reference_id: String(requestRow.request_id) };

    if (!skipDebit) {
      const debitRes = await fetch(`/api/v1/pms/folios/${folioId}/postings`, {
        method: 'POST', headers: headers(true),
        body: JSON.stringify({
          posting_type: postingType, source_module: sourceModule, debit_amount: amount,
          revenue_code: revenueCode, remarks: `Guest service: ${requestRow.request_type || ''}`.trim(), ...refBody
        })
      });
      const debitData = await debitRes.json();
      if (!debitRes.ok) throw new Error(debitData?.error || 'Failed to post charge to folio');
    }

    if (mode === 'PAY_NOW') {
      const creditRes = await fetch(`/api/v1/pms/folios/${folioId}/postings`, {
        method: 'POST', headers: headers(true),
        body: JSON.stringify({
          posting_type: 'PAYMENT', source_module: sourceModule, credit_amount: amount, payment_mode: payMode,
          remarks: `Payment for guest service: ${requestRow.request_type || ''}`.trim(), ...refBody
        })
      });
      const creditData = await creditRes.json();
      if (!creditRes.ok) throw new Error(creditData?.error || 'Charge posted, but collecting payment failed — use Complete Billing to retry');
    }
    return true;
  };

  // Retry for a PARTIAL row (debit posted, PAY_NOW credit missing) — re-issues
  // only the missing credit leg, using the room's payment mode default (CASH);
  // staff can re-run this after confirming how the guest actually paid.
  const completeBilling = async (row) => {
    setBillingBusyId(row.request_id);
    setError(''); setMessage('');
    try {
      const svc = guestServices.find(s => String(s.id) === String(row.service_id));
      await postBillingForRequest(row, {
        stayId: row.stay_id, revenueCode: svc?.revenue_code, postingType: svc?.posting_type,
        sourceModule: svc?.source_module || 'GUEST_SERVICE', amount: row.requested_amount,
        mode: 'PAY_NOW', payMode: 'CASH', skipDebit: true
      });
      setMessage('Billing completed');
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to complete billing');
    } finally {
      setBillingBusyId(null);
    }
  };

  const submitRequest = async () => {
    if (!formData.room_id) { setError('Please select a room'); return; }
    if (formType === 'SERVICE' && !formData.request_type) { setError('Please select a request type'); return; }
    if (formType === 'HK' && !formData.request_type) { setError('Please select a request type'); return; }
    if (formType === 'SERVICE' && billable && !billingServiceId) { setError('Please select a billable service, or turn off "Bill this service"'); return; }
    setFormLoading(true);
    setError('');
    try {
      const endpoint = formType === 'SERVICE'
        ? '/api/v1/pms/guest-requests/create'
        : '/api/v1/pms/housekeeping/requests/create';
      const billingBody = (formType === 'SERVICE' && billable)
        ? { service_id: Number(billingServiceId), fulfilment_mode: fulfilmentMode, requested_amount: billingAmount }
        : {};
      const body = formType === 'SERVICE'
        ? { room_id: Number(formData.room_id), request_category: formData.request_category, request_type: formData.request_type, quantity: formData.quantity, priority: formData.priority, remarks: formData.remarks, ...billingBody }
        : { room_id: Number(formData.room_id), request_type: formData.request_type, quantity: formData.quantity, priority: formData.priority, remarks: formData.remarks };
      const res = await fetch(endpoint, { method: 'POST', headers: headers(true), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to create request');

      if (formType === 'SERVICE' && billable) {
        const room = occupiedRooms.find(r => String(r.room_id) === String(formData.room_id));
        try {
          await postBillingForRequest(data, {
            stayId: room?.stay_id, revenueCode: data.revenue_code, postingType: data.posting_type,
            sourceModule: data.source_module, amount: data.requested_amount,
            mode: fulfilmentMode, payMode: paymentMode
          });
          setMessage('Service request created and billed successfully');
        } catch (billingErr) {
          setError(billingErr?.message || 'Request created, but billing failed — use Complete Billing on this row to retry');
        }
      } else {
        setMessage(`${formType === 'SERVICE' ? 'Service' : 'Housekeeping'} request created successfully`);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to create request');
    } finally {
      setFormLoading(false);
    }
  };

  const updateStatus = async (requestId, newStatus, type = 'hk') => {
    setError('');
    setMessage('');
    const endpoint = type === 'service'
      ? `/api/v1/pms/housekeeping/service-requests/${requestId}/status`
      : `/api/v1/pms/housekeeping/requests/${requestId}/status`;
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: headers(true),
        body: JSON.stringify({ status: newStatus })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to update');
      setMessage(`Request updated to ${newStatus.replace(/_/g, ' ')}`);
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to update');
    }
  };

  const tabBtn = (active) => ({
    padding: '0.4rem 0.75rem', borderRadius: 6, border: '1px solid #cbd5e0',
    background: active ? 'var(--theme-primary)' : '#fff', color: active ? '#fff' : '#1f2937',
    cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem'
  });

  const thStyle = { padding: '0.45rem 0.5rem', textAlign: 'left', fontSize: '0.82rem' };
  const tdStyle = { padding: '0.4rem 0.5rem', fontSize: '0.83rem' };
  const inputStyle = { width: '100%', padding: '0.4rem 0.5rem', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' };

  const ActionButtons = ({ status, onAction }) => (
    <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
      {status === 'OPEN' && (
        <button onClick={() => onAction('IN_PROGRESS')}
          style={{ fontSize: '0.75rem', padding: '2px 8px', background: 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Start</button>
      )}
      {['OPEN', 'IN_PROGRESS'].includes(status) && (
        <button onClick={() => onAction('DONE')}
          style={{ fontSize: '0.75rem', padding: '2px 8px', background: '#166534', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Done</button>
      )}
      {['OPEN', 'IN_PROGRESS'].includes(status) && (
        <button onClick={() => onAction('CANCELLED')}
          style={{ fontSize: '0.75rem', padding: '2px 8px', background: '#b91c1c', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
      )}
      {['DONE', 'CANCELLED'].includes(status) && (
        <span style={{ fontSize: '0.75rem', color: 'var(--theme-text-muted)' }}>—</span>
      )}
    </div>
  );

  const guestPortalHkRequests = hkRequests.filter(r => r.source_module === 'GUEST_PORTAL' || r.created_by_type === 'STAFF');
  const openHkCount = hkRequests.filter(r => r.request_status === 'OPEN' && (r.source_module === 'GUEST_PORTAL' || r.created_by_type === 'STAFF')).length;
  const openSvcCount = serviceRequests.filter(r => r.request_status === 'OPEN').length;

  const availableTypes = formType === 'SERVICE' ? (SERVICE_TYPES[formData.request_category] || []) : HK_TYPES;

  return (
    <div className="page-shell">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.6rem', alignItems: isMobile ? 'flex-start' : 'center', flexWrap: 'wrap', marginBottom: '0.6rem', flexDirection: isMobile ? 'column' : 'row' }}>
        <h3 style={{ margin: 0, color: 'var(--theme-primary)', fontSize: isMobile ? '0.95rem' : undefined }}>Front Office - Guest Requests</h3>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', width: isMobile ? '100%' : undefined }}>
          <button onClick={() => openForm('SERVICE')}
            style={{ padding: '0.3rem 0.7rem', background: '#166534', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
            + Service Request
          </button>
          <button onClick={() => openForm('HK')}
            style={{ padding: '0.3rem 0.7rem', background: '#1e40af', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
            + HK Request
          </button>
          <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>Status:</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '0.25rem 0.5rem', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: '0.82rem' }}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
          <button onClick={load} style={{ padding: '0.3rem 0.7rem', background: 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.82rem' }}>
            Refresh
          </button>
        </div>
      </div>

      {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.5rem', fontSize: '0.85rem' }}>{error}</div>}
      {message && <div style={{ background: '#f0fdf4', color: '#166534', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.5rem', fontSize: '0.85rem' }}>{message}</div>}

      {loading && <div style={{ color: 'var(--theme-text-secondary)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Loading...</div>}

      {/* New Request Form */}
      {showForm && (
        <div className="card card-body" style={{ marginBottom: '0.8rem', border: '2px solid var(--theme-primary)', background: '#f8fafc' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
            <div style={{ fontWeight: 700, color: 'var(--theme-primary)', fontSize: '0.95rem' }}>
              {formType === 'SERVICE' ? 'New Service Request (Staff)' : 'New Housekeeping Request (Staff)'}
            </div>
            <button onClick={() => setShowForm(false)}
              style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' }}>x</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.6rem' }}>
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Room *</label>
              <select value={formData.room_id} onChange={e => setFormData(f => ({ ...f, room_id: e.target.value }))} style={inputStyle}>
                <option value="">-- Select Room --</option>
                {occupiedRooms.map(rm => (
                  <option key={rm.room_id} value={rm.room_id}>
                    {rm.room_number} — {(rm.guest_name || '').trim()}
                  </option>
                ))}
              </select>
            </div>
            {formType === 'SERVICE' && (
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Category *</label>
                <select value={formData.request_category}
                  onChange={e => setFormData(f => ({ ...f, request_category: e.target.value, request_type: '' }))}
                  style={inputStyle}>
                  {['RECEPTION', 'MAINTENANCE', 'AMENITY', 'OTHER'].map(c => (
                    <option key={c} value={c}>{c.charAt(0) + c.slice(1).toLowerCase()}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Request Type *</label>
              <select value={formData.request_type} onChange={e => setFormData(f => ({ ...f, request_type: e.target.value }))} style={inputStyle}>
                <option value="">-- Select Type --</option>
                {availableTypes.map(t => (
                  <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Quantity</label>
              <input type="number" min={1} max={20} value={formData.quantity}
                onChange={e => setFormData(f => ({ ...f, quantity: Math.max(1, Number(e.target.value) || 1) }))}
                style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Priority</label>
              <select value={formData.priority} onChange={e => setFormData(f => ({ ...f, priority: e.target.value }))} style={inputStyle}>
                <option value="NORMAL">Normal</option>
                <option value="EXPRESS">Express</option>
                <option value="VIP">VIP</option>
              </select>
            </div>
            <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Remarks</label>
              <input type="text" value={formData.remarks} onChange={e => setFormData(f => ({ ...f, remarks: e.target.value }))}
                placeholder="Optional notes..." style={inputStyle} />
            </div>
          </div>

          {formType === 'SERVICE' && (
            <div style={{ marginTop: '0.6rem', padding: '0.6rem', background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 6 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                <input type="checkbox" checked={billable} onChange={e => setBillable(e.target.checked)} />
                Bill this service
              </label>
              {billable && (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.6rem', marginTop: '0.5rem' }}>
                  <div>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Service *</label>
                    <select value={billingServiceId} onChange={e => onBillingServiceChange(e.target.value)} style={inputStyle}>
                      <option value="">-- Select Service --</option>
                      {guestServices.map(s => (
                        <option key={s.id} value={s.id}>{s.service_name} (₹{Number(s.rate_amount || 0).toFixed(2)})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Amount</label>
                    <input type="number" min="0" step="0.01" value={billingAmount} onChange={e => setBillingAmount(Number(e.target.value) || 0)} style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Fulfilment</label>
                    <select value={fulfilmentMode} onChange={e => setFulfilmentMode(e.target.value)} style={inputStyle}>
                      <option value="CHARGE_TO_ROOM">Charge to Room</option>
                      <option value="PAY_NOW">Pay Now</option>
                    </select>
                  </div>
                  {fulfilmentMode === 'PAY_NOW' && (
                    <div>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Payment Mode</label>
                      <select value={paymentMode} onChange={e => setPaymentMode(e.target.value)} style={inputStyle}>
                        {['CASH', 'CARD', 'UPI', 'BANK_TRANSFER'].map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.5rem' }}>
            <button onClick={submitRequest} disabled={formLoading}
              style={{ padding: '0.4rem 1.2rem', background: 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', opacity: formLoading ? 0.6 : 1 }}>
              {formLoading ? 'Creating...' : 'Create Request'}
            </button>
            <button onClick={() => setShowForm(false)}
              style={{ padding: '0.4rem 1rem', background: '#e2e8f0', color: '#1f2937', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.85rem' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.8rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 140, background: openHkCount > 0 ? '#fef3c7' : '#f0fdf4', borderRadius: 8, padding: '0.7rem 1rem', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: openHkCount > 0 ? '#92400e' : '#166534' }}>{openHkCount}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>Open HK Requests</div>
        </div>
        <div style={{ flex: 1, minWidth: 140, background: openSvcCount > 0 ? '#fef3c7' : '#f0fdf4', borderRadius: 8, padding: '0.7rem 1rem', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: openSvcCount > 0 ? '#92400e' : '#166534' }}>{openSvcCount}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>Open Service Requests</div>
        </div>
      </div>

      {/* Service Requests (main focus for Front Office) */}
      <div className="card card-body" style={{ marginBottom: '0.8rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.45rem', flexWrap: 'wrap', gap: '0.4rem' }}>
          <div style={{ fontWeight: 700, color: 'var(--theme-primary)' }}>Guest Service Requests</div>
          <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
            {CATEGORIES.map(cat => (
              <button key={cat} onClick={() => setCategoryFilter(cat)} style={tabBtn(categoryFilter === cat)}>
                {cat === 'ALL' ? 'All' : cat.charAt(0) + cat.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </div>
        <div style={{ overflowX: 'auto', maxWidth: isMobile ? '100%' : undefined }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#334155', color: '#fff' }}>
                <th style={thStyle}>Room</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Request</th>
                <th style={thStyle}>Qty</th>
                <th style={thStyle}>Priority</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Fulfilment</th>
                <th style={thStyle}>Billing</th>
                <th style={thStyle}>Created By</th>
                <th style={thStyle}>Remarks</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {serviceRequests.map(sr => (
                <tr key={sr.request_id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{sr.room_number || '-'}</td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: '0.72rem', padding: '1px 6px', borderRadius: 4, fontWeight: 600,
                      background: sr.request_category === 'RECEPTION' ? '#dbeafe' : sr.request_category === 'MAINTENANCE' ? '#fef3c7' : sr.request_category === 'AMENITY' ? '#d1fae5' : 'var(--theme-border-light)',
                      color: sr.request_category === 'RECEPTION' ? '#1e40af' : sr.request_category === 'MAINTENANCE' ? '#92400e' : sr.request_category === 'AMENITY' ? '#065f46' : '#475569' }}>
                      {sr.request_category}
                    </span>
                  </td>
                  <td style={tdStyle}>{(sr.request_type || '').replace(/_/g, ' ')}</td>
                  <td style={tdStyle}>{sr.quantity || 1}</td>
                  <td style={tdStyle}>{sr.priority}</td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: statusColor(sr.request_status) }}>
                      {(sr.request_status || '').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td style={tdStyle}>{sr.service_id ? `₹${Number(sr.requested_amount || 0).toFixed(2)}` : '-'}</td>
                  <td style={tdStyle}>{sr.fulfilment_mode ? sr.fulfilment_mode.replace(/_/g, ' ') : '-'}</td>
                  <td style={tdStyle}>
                    {sr.billing_status ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <span style={{
                          fontSize: '0.72rem', padding: '1px 6px', borderRadius: 4, fontWeight: 600,
                          background: sr.billing_status === 'POSTED' ? '#d1fae5' : sr.billing_status === 'PARTIAL' ? '#fee2e2' : '#fef3c7',
                          color: sr.billing_status === 'POSTED' ? '#065f46' : sr.billing_status === 'PARTIAL' ? '#991b1b' : '#92400e'
                        }}>
                          {sr.billing_status}
                        </span>
                        {sr.billing_status === 'PARTIAL' && (
                          <button onClick={() => completeBilling(sr)} disabled={billingBusyId === sr.request_id}
                            style={{ fontSize: '0.72rem', padding: '2px 6px', background: '#1e40af', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                            {billingBusyId === sr.request_id ? 'Posting…' : 'Complete Billing'}
                          </button>
                        )}
                      </div>
                    ) : '-'}
                  </td>
                  <td style={tdStyle}>
                    <CreatedByBadge type={sr.created_by_type} name={sr.created_by_name} timestamp={sr.created_at} />
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--theme-text-secondary)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sr.remarks || '-'}</td>
                  <td style={tdStyle}>
                    <ActionButtons status={sr.request_status} onAction={(s) => updateStatus(sr.request_id, s, 'service')} />
                  </td>
                </tr>
              ))}
              {!serviceRequests.length && (
                <tr><td colSpan={12} style={{ padding: '0.7rem', color: 'var(--theme-text-muted)', textAlign: 'center' }}>No guest service requests</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Housekeeping Requests */}
      <div className="card card-body">
        <div style={{ fontWeight: 700, color: 'var(--theme-primary)', marginBottom: '0.45rem' }}>Housekeeping Requests</div>
        <div style={{ overflowX: 'auto', maxWidth: isMobile ? '100%' : undefined }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
                <th style={thStyle}>Room</th>
                <th style={thStyle}>Request Type</th>
                <th style={thStyle}>Qty</th>
                <th style={thStyle}>Priority</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Created By</th>
                <th style={thStyle}>Remarks</th>
                <th style={thStyle}>Rating</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {guestPortalHkRequests.map(r => (
                <tr key={r.request_id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{r.room_number || '-'}</td>
                  <td style={tdStyle}>{(r.request_type || '').replace(/_/g, ' ')}</td>
                  <td style={tdStyle}>{r.quantity || 1}</td>
                  <td style={tdStyle}>{r.priority}</td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: statusColor(r.request_status) }}>
                      {(r.request_status || '').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <CreatedByBadge type={r.created_by_type} name={r.created_by_name} timestamp={r.created_at} />
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--theme-text-secondary)' }}>{r.remarks || '-'}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {r.feedback_rating ? (
                      <span style={{ fontSize: 13, letterSpacing: 1 }}>
                        {[1,2,3,4,5].map(s => <span key={s} style={{ color: s <= r.feedback_rating ? '#f59e0b' : '#d1d5db' }}>&#9733;</span>)}
                      </span>
                    ) : <span style={{ color: '#cbd5e1', fontSize: '0.75rem' }}>-</span>}
                  </td>
                  <td style={tdStyle}>
                    <ActionButtons status={r.request_status} onAction={(s) => updateStatus(r.request_id, s, 'hk')} />
                  </td>
                </tr>
              ))}
              {!guestPortalHkRequests.length && (
                <tr><td colSpan={9} style={{ padding: '0.7rem', color: 'var(--theme-text-muted)', textAlign: 'center' }}>No housekeeping requests</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ textAlign: 'center', color: 'var(--theme-text-muted)', fontSize: '0.75rem', marginTop: '0.6rem' }}>Auto-refreshes every 30 seconds</div>
    </div>
  );
}

export default PmsGuestRequests;
